/**
 * The first-run handoff: the plaintext password of a mailbox the operator has just created, carried
 * from the grid (where the create form ran) to the mailbox page (where it is shown), IN MEMORY ONLY.
 *
 * Why not navigation state: `location.state` is backed by `history.state`, which the browser
 * persists across a reload, so a password there would be at rest on disk, contradicting the store's
 * "never persisted" promise. Why not the reducer: each route mounts its own, so the grid's secrets
 * die when it unmounts. A module-level map dies with the tab, which is exactly the behaviour the
 * one-time warning promises: leave, and the password is masked for good.
 *
 * The stash survives until the mailbox page TAKES it, so "create, leave before the page opens, open
 * Setup Mailbox from the tile later" still gets the first-run treatment in the same tab. A refresh
 * loses it, correctly.
 */

/** Keyed by the mailbox's address. */
const stash = new Map<string, string>();

/** Remember a just-created mailbox's plaintext until its page mounts. */
export const stashFirstRun = (email: string, secret: string): void => {
  stash.set(email, secret);
};

/** Take (and forget) the plaintext for a mailbox, or `null` when none is held. */
export const takeFirstRun = (email: string): string | null => {
  const secret = stash.get(email) ?? null;
  stash.delete(email);
  return secret;
};

/** Whether a first-run plaintext is held for a mailbox, without taking it. */
export const hasFirstRun = (email: string): boolean => stash.has(email);
